import React, { useMemo, useState } from 'react';
import { UserOutlined } from '@ant-design/icons';
import { Avatar, Col, Popover, Row } from 'antd';
import InputSearchSuffixIcon from 'components/InputSearchSuffixIcon';
import appointmentActions from 'features/appointment/services/actions';
import appointmentSelectors from 'features/appointment/services/selectors';
import { IStaffItemData } from 'services/shop/types/staff';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';

const SelectStaff = () => {
  const staffListStore = appointmentSelectors.getListStaffAvailableBooking();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();

  const staffList = useMemo(
    () => staffListStore?.filter((o) => !o.anyBody) || [],
    [staffListStore]
  );

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleClickStaff = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    staff: IStaffItemData
  ) => {
    e.stopPropagation();
    setOpen(false);
    setSearch(staff.firstName);
    dispatch(appointmentActions.setStaffFilter(staff));
  };

  const changeKeyWord = (keyword: string) => {
    const trimmed = keyword.trim().toLowerCase();
    const noAccent = trimmed
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd');
    return noAccent.replace(/\s+/g, '');
  };

  const highlightMatch = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    if (parts.length === 1) return text;
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.trim().toLowerCase() ? (
        <span key={i} className="highlight">{part}</span>
      ) : (
        part
      )
    );
  };

  const handleChangeSearch = (value: string) => {
    setSearch(value);
    if (!value) {
      dispatch(appointmentActions.setStaffFilter(null));
    }
  };

  return (
    <Popover
      placement='bottomLeft'
      arrow={false}
      overlayInnerStyle={{
        padding: 0,
      }}
      content={
        <ContentContainer>
          <Row gutter={[12, 12]}>
            {staffList.map((o) => {
              const isMatch = search && changeKeyWord(o.firstName).includes(changeKeyWord(search));
              return (
                <Col
                  key={o.id}
                  span={3}
                  onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleClickStaff(e, o)}
                >
                  <LabelStaffItem className={isMatch ? 'match' : ''}>
                    <Avatar
                      src={o.urlImage || undefined}
                      size={87}
                      icon={<UserOutlined />}
                      shape='circle' />
                    <div className={`title ${isMatch ? 'match' : ''}`}>
                      {isMatch ? highlightMatch(o.firstName, search) : o.firstName}
                    </div>
                  </LabelStaffItem>
                </Col>
              );
            })}
          </Row>
        </ContentContainer>
      }
      trigger='click'
      open={open}
      onOpenChange={handleOpenChange}
    >
      <div style={{ minWidth: '400px' }}>
        <InputSearchSuffixIcon value={search} onChange={handleChangeSearch} />
      </div>
    </Popover>
  );
};

export default SelectStaff;

const ContentContainer = styled.div`
  width: 60vw;
  height: 60vh;
  padding: 12px;
  overflow: auto;
`;

const LabelStaffItem = styled.div`
  cursor: pointer;
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  border: 2px solid #e9edf2;
  background: #f6f7fc;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &.match {
    border-color: #ff7d00;
    box-shadow: 0 0 0 1px rgba(255, 125, 0, 0.15);
  }

  .title {
    color: #1d2129;
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    text-transform: uppercase;

    .highlight {
      color: #ff7d00;
      font-weight: 700;
    }
  }
`;
